import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    Shield, ChevronLeft, Search, RefreshCw, AlertTriangle, 
    CheckCircle, XCircle, LogIn, LogOut, Key, UserPlus, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SecurityLogs() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterAction, setFilterAction] = useState('all');
    const [limit, setLimit] = useState(50);

    useEffect(() => {
        if (user?.role !== 'admin') {
            navigate('/dashboard');
            return;
        }
        loadLogs();
    }, [user, limit]);

    async function loadLogs() {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/admin/audit-logs?limit=${limit}`);
            setLogs(res.data.logs || []);
            setTotal(res.data.total || 0);
        } catch (e) {
            toast.error('Failed to load security logs');
        } finally {
            setLoading(false);
        }
    }

    function formatDate(dateStr) {
        if (!dateStr) return 'Unknown';
        return new Date(dateStr).toLocaleString();
    }

    function formatAction(action) {
        const actionMap = {
            'login_success': { text: 'Login', color: 'bg-green-100 text-green-700', icon: LogIn },
            'login_failed': { text: 'Failed Login', color: 'bg-red-100 text-red-700', icon: XCircle },
            'logout': { text: 'Logout', color: 'bg-stone-100 text-stone-700', icon: LogOut },
            'register': { text: 'Registration', color: 'bg-blue-100 text-blue-700', icon: UserPlus },
            'register_with_invite': { text: 'Invited User', color: 'bg-blue-100 text-blue-700', icon: UserPlus },
            'password_changed': { text: 'Password Changed', color: 'bg-amber-100 text-amber-700', icon: Key },
            'password_reset_requested': { text: 'Password Reset Requested', color: 'bg-amber-100 text-amber-700', icon: Key },
            'password_reset_completed': { text: 'Password Reset Completed', color: 'bg-green-100 text-green-700', icon: Key },
            'session_terminated': { text: 'Session Ended', color: 'bg-orange-100 text-orange-700', icon: LogOut },
            'all_sessions_terminated': { text: 'All Sessions Ended', color: 'bg-orange-100 text-orange-700', icon: LogOut },
            'login_2fa_pending': { text: '2FA Pending', color: 'bg-purple-100 text-purple-700', icon: Shield },
            'login_2fa_success': { text: '2FA Verified', color: 'bg-green-100 text-green-700', icon: CheckCircle },
            '2fa_failed': { text: '2FA Failed', color: 'bg-red-100 text-red-700', icon: XCircle },
            'login_blocked_ip': { text: 'IP Blocked', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
        };
        return actionMap[action] || { text: action.replace(/_/g, ' '), color: 'bg-stone-100 text-stone-700', icon: Shield };
    }

    // Filter logs based on search and action filter
    const filteredLogs = logs.filter(log => {
        const matchesSearch = !searchTerm || 
            log.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.ip_address?.includes(searchTerm) ||
            log.action?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesAction = filterAction === 'all' || log.action === filterAction;
        
        return matchesSearch && matchesAction;
    });

    // Get unique actions for filter dropdown
    const uniqueActions = [...new Set(logs.map(l => l.action))].sort();

    if (user?.role !== 'admin') {
        return null;
    }

    return (
        <div className="min-h-screen bg-[#FDFCF8]">
            {/* Header */}
            <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
                <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/dashboard')}
                            className="rounded-none"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </Button>
                        <div className="flex items-center gap-3">
                            <Shield className="h-6 w-6 text-amber-600" />
                            <h1 className="font-serif text-xl font-semibold">Security Audit Logs</h1>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        onClick={loadLogs}
                        disabled={loading}
                        className="rounded-none"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <Card className="rounded-none border-stone-200">
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-stone-900">{total}</div>
                            <div className="text-sm text-stone-500">Total Events</div>
                        </CardContent>
                    </Card>
                    <Card className="rounded-none border-stone-200">
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-green-600">
                                {logs.filter(l => l.action === 'login_success').length}
                            </div>
                            <div className="text-sm text-stone-500">Successful Logins</div>
                        </CardContent>
                    </Card>
                    <Card className="rounded-none border-stone-200">
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-red-600">
                                {logs.filter(l => l.action === 'login_failed').length}
                            </div>
                            <div className="text-sm text-stone-500">Failed Logins</div>
                        </CardContent>
                    </Card>
                    <Card className="rounded-none border-stone-200">
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-purple-600">
                                {logs.filter(l => l.action?.includes('2fa')).length}
                            </div>
                            <div className="text-sm text-stone-500">2FA Events</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters */}
                <Card className="rounded-none border-stone-200 mb-6">
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                                <Input
                                    placeholder="Search by email, IP address, or action..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 rounded-none"
                                />
                            </div>
                            <select
                                value={filterAction}
                                onChange={(e) => setFilterAction(e.target.value)}
                                className="h-10 px-3 border border-stone-200 bg-white rounded-none min-w-[200px]"
                            >
                                <option value="all">All Actions</option>
                                {uniqueActions.map(action => (
                                    <option key={action} value={action}>
                                        {formatAction(action).text}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={limit}
                                onChange={(e) => setLimit(Number(e.target.value))}
                                className="h-10 px-3 border border-stone-200 bg-white rounded-none"
                            >
                                <option value={50}>Last 50</option>
                                <option value={100}>Last 100</option>
                                <option value={200}>Last 200</option>
                                <option value={500}>Last 500</option>
                            </select>
                        </div>
                    </CardContent>
                </Card>

                {/* Logs Table */}
                <Card className="rounded-none border-stone-200">
                    <CardHeader>
                        <CardTitle className="font-serif text-lg">
                            Event Log ({filteredLogs.length} of {total})
                        </CardTitle>
                        <CardDescription>
                            Security events including logins, logouts, password changes, and 2FA activity
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                            </div>
                        ) : filteredLogs.length === 0 ? (
                            <div className="text-center py-12 text-stone-500">
                                No security events found
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-stone-200">
                                            <th className="text-left py-3 px-4 text-sm font-medium text-stone-500">Timestamp</th>
                                            <th className="text-left py-3 px-4 text-sm font-medium text-stone-500">Action</th>
                                            <th className="text-left py-3 px-4 text-sm font-medium text-stone-500">User</th>
                                            <th className="text-left py-3 px-4 text-sm font-medium text-stone-500">IP Address</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredLogs.map((log, idx) => {
                                            const actionInfo = formatAction(log.action);
                                            const Icon = actionInfo.icon;
                                            const isFailure = log.action?.includes('failed') || log.action?.includes('blocked');
                                            
                                            return (
                                                <motion.tr
                                                    key={log.id || idx}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: idx * 0.02 }}
                                                    className={`border-b border-stone-100 hover:bg-stone-50 ${isFailure ? 'bg-red-50/50' : ''}`}
                                                >
                                                    <td className="py-3 px-4 text-sm text-stone-600">
                                                        {formatDate(log.timestamp)}
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <Badge className={`rounded-none text-xs ${actionInfo.color}`}>
                                                            <Icon className="h-3 w-3 mr-1" />
                                                            {actionInfo.text}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-sm font-medium">
                                                        {log.user_email || 'Unknown'}
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-stone-500 font-mono">
                                                        {log.ip_address || 'Unknown'}
                                                    </td>
                                                </motion.tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
